<template>
  <BaseClearButton @click="ping()" color="light" size="small" class="text-nowrap">
    <div class="flex items-center overflow-hidden w-12">
      <i class="icon-radio text-blue-500 mr-1"></i>
      <Transition name="fade" mode="out-in">
        <div class="flex items-center justify-start" v-if="!pinging">Ping</div>
        <div class="flex items-center justify-start" v-else>
          <IonSpinner name="dots" class="size-[20px]"></IonSpinner>
        </div>
      </Transition>
    </div>
  </BaseClearButton>
</template>

<script setup lang="ts">
import { useToast } from '@/composables/use-toast';
import BaseClearButton from '../forms/BaseClearButton.vue';
import { useLiveDevices } from '@/http-services/live-device.service';
import {
  GatewayCommandMessage,
  GatewayCommandMessageType,
  GatewayResponseMessage,
  GatewayResponseMessageType,
} from '@ncss/models';
import { HydrateRead, RE2Read, RE4Read, TR4ERead, Read } from '@ncss/models/dist/v3';
import { ref } from 'vue';
import { IonSpinner } from '@ionic/vue';

const props = defineProps<{
  gatewayId: number;
  deviceId: number;
}>();

const emit = defineEmits<{
  (e: 'onPing', read: Read): void;
}>();

const { successToast, errorToast } = useToast();
const { start, stop, onMessage, sendMessage } = useLiveDevices();
const pinging = ref(false);

async function ping() {
  if (!props.gatewayId || !props.deviceId || pinging.value) {
    return;
  }
  pinging.value = true;
  const res = await new Promise<{ error?: string; read?: RE2Read | RE4Read | TR4ERead | null }>((resolve) => {
    const timer = setTimeout(() => {
      resolve({ error: `Timed out waiting for ${props.deviceId.toString(16).toUpperCase()}` });
    }, 30_000);
    start(props.gatewayId);
    onMessage((r) => {
      if (
        r instanceof GatewayResponseMessage &&
        r.type === GatewayResponseMessageType.PING_POWERED_DEVICE_RESPONSE &&
        r.pingedDevice?.id === props.deviceId
      ) {
        clearTimeout(timer);
        resolve({ read: HydrateRead(r.pingedDevice.read) as any });
      }
    });
    const msg = new GatewayCommandMessage({
      type: GatewayCommandMessageType.PING_POWERED_DEVICE,
      gatewayId: props.gatewayId,
    });
    msg.deviceId = props.deviceId;
    sendMessage(msg);
  });
  if (res.error) {
    errorToast(res.error);
  } else if (res.read) {
    successToast(`Successfully pinged ${res.read.deviceId.toString(16).toUpperCase()}`);
    emit('onPing', res.read);
  }
  stop();
  pinging.value = false;
}
</script>

<style scoped></style>
